import get from 'lodash/get';
import { lazy, Suspense } from 'react';

import { Embed } from '@hivebrite/design-system/Embed';
import type { EmbedComponentProps } from '@hivebrite/design-system/Embed';
import { useTheme } from '@hivebrite/design-system/Theme';

import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';

// Fake function to add necessary translations for DS/Embed component.
export const translations = ({ providerName, title }) => [
  s__('Embed|Show third-party media?'),
  s__(
    'Embed|This media may use cookies and has its own privacy policy. You always can change your choice in privacy settings of your account.',
  ),
  s__('Embed|Always for %{providerName}').i__({ providerName }),
  s__('Embed|Allow once'),
  s__('Embed|Remove embed preview of %{title}.').i__({ title }),
  s__('Embed|Start the video preview'),
];

const EmbedLy = lazy(
  () => import('hb-react/shared/components/DesignSystem/EmbedLy'),
);

const Wrapper = (props: EmbedComponentProps) => {
  const theme = useTheme();
  const { flagUeDsEmbed } = useFeatureFlag();
  const { currentNetwork } = useDefaultAppContexts();

  // Do not use destructuring. In some cases BO.currentNetwork
  // may be undefined for awhile.
  const displayThirdPartyConsent = get(
    currentNetwork,
    'features.cookies.config.display_third_party_consent',
  );

  // Just pass props to <Embed /> when flag-ue-ds-embed cleaned up.
  const {
    autoplay,
    embed,
    isDisabled,
    isLoading,
    linkUrl,
    mediaAspectRatio,
    onClose,
    onVideoPlay,
    renderEmptyPreview,
    roundCorners = 'small',
    withDescription = true,
  } = props;

  const roundedEmbed =
    roundCorners === 'themeDefault'
      ? `${theme.themeVariables.globalBorderRadius}px`
      : roundCorners === 'small';

  const v1Props = {
    autoplay,
    customRenderForEmptyImage: renderEmptyPreview,
    embed,
    externalUrl: linkUrl,
    forceRatio: mediaAspectRatio,
    hideDescription: !withDescription,
    isLoading,
    isDisabled,
    onClose,
    openPlayVideo: onVideoPlay,
    roundedEmbed,
  };

  return flagUeDsEmbed ? (
    <Embed {...props} withUserConsent={displayThirdPartyConsent} />
  ) : (
    <Suspense fallback={null}>
      <EmbedLy {...v1Props} />
    </Suspense>
  );
};

export default Wrapper;
